import {
  forwardRef,
  useContext,
  type ComponentPropsWithoutRef,
  type MouseEventHandler,
} from "react";
import { DataTrackingId } from "../../types";
import { DetailsContext } from "./internal/DetailsContext";
import { childrenToString } from "../../internal/functions";
import { useTrackingId } from "../../hooks";
import { DetailsArrow } from "./internal/DetailsArrow";

import cx from "classnames";
import styles from "./Details.module.scss";

/**
 * Props for the DetailsSummary component
 * @extends ComponentPropsWithoutRef<"button">
 */
export type DetailsSummaryProps = ComponentPropsWithoutRef<"button"> &
  DataTrackingId;

/**
 * DetailsSummary component for the clickable header of a Details section.
 *
 * Features:
 * - Clickable button that toggles the Details expanded state
 * - Automatic ARIA attributes for accessibility
 * - Includes animated arrow indicator
 * - Supports custom onClick handlers
 * - Keyboard navigation support (Enter, Space)
 * - Styled with consistent typography and spacing
 *
 * @example
 * <Details.Summary onClick={() => console.log('Summary clicked')}>
 *   Click to expand content
 * </Details.Summary>
 */
export const DetailsSummary = forwardRef<
  HTMLButtonElement,
  DetailsSummaryProps
>(function DetailsSummary(
  { children, onClick: onClickProp, className: classNameProp, ...rest },
  ref,
) {
  const { id, isExpanded, toggle } = useContext(DetailsContext);

  const handleClick: MouseEventHandler<HTMLButtonElement> = (...args) => {
    onClickProp?.(...args);
    toggle();
  };

  const data = {
    children: childrenToString(children),
  };

  const trackingId = useTrackingId({
    name: "DetailsSummary",
    data,
    hasOverride: !!rest["data-tracking-id"],
  });

  const className = cx(styles.summary, classNameProp);

  return (
    <button
      data-tracking-id={trackingId}
      data-anv="details-summary"
      aria-controls={id}
      aria-expanded={isExpanded}
      className={className}
      onClick={handleClick}
      ref={ref}
      {...rest}
    >
      <DetailsArrow />
      {children}
    </button>
  );
});
